import { useEffect, useState } from 'react';
import LinkExternal from '_/images/bf2026/link-external.svg';
import { CLOSED_KEY_PREFIX } from './closedBannersScript';

export type BlackFridayBanner = {
  variant: 'bf2026';
  name: string;
  startDate: string;
  endDate: string;
  link: { url: string };
  badge: string;
  live?: boolean;
  texts: { md: string; sm: string; buttonText: string };
};

export default function BlackFridayBar({ banner, isTestMode }: { banner: BlackFridayBanner; isTestMode: boolean }) {
  const storageKey = CLOSED_KEY_PREFIX + banner.name;
  const [closed, setClosed] = useState(false);

  // Fechar vale só para esta fase: a próxima fase tem outro name e volta a aparecer.
  // Em modo teste o fechado salvo é ignorado e o X fecha só nesta página.
  useEffect(() => {
    if (isTestMode) {
      setClosed(false);
      return;
    }
    try {
      setClosed(window.localStorage.getItem(storageKey) === '1');
    } catch {
      setClosed(false);
    }
  }, [storageKey, isTestMode]);

  const close = () => {
    setClosed(true);
    if (isTestMode) return;
    try {
      window.localStorage.setItem(storageKey, '1');
    } catch {
      // storage bloqueado: fecha só nesta página
    }
  };

  if (closed) return null;

  // Com skin o placeholder do topo some (+div+main) e o conteúdo desce junto com o anúncio.
  // O Topo Mobile fixado por script externo (top:60px) desce para baixo da barra.
  return (
    <section
      data-banner={banner.name}
      className="fixed left-0 top-0 z-[988] mt-[60px] h-[50px] min-h-[50px] w-full overflow-hidden bg-[#06122B] font-primary md:mt-0 tablet:pl-[60px] [&+div+div]:mt-[50px] [&+div+main]:pt-[50px] [&+div]:mt-[50px] [&~div>div:has(>[id$=topo-mobile])]:!top-[110px]">
      {isTestMode && <div className="bg-red-500 absolute left-2 top-1 z-10 rounded px-2 py-1 text-[10px] font-bold text-white">TESTE: {banner.name}</div>}
      <a
        href={banner.link.url}
        target="_blank"
        rel="noopener noreferrer"
        title={banner.texts.md}
        className="flex h-full items-center justify-center gap-2 pl-3 pr-10 text-[13px] font-bold text-white no-underline max-[359px]:gap-1 max-[359px]:pl-2 tablet:gap-4 tablet:text-base">
        <img
          src="/images/bf2026/black-friday.png"
          alt="Black Friday"
          width={242}
          height={100}
          className="mr-2 h-[25px] w-auto shrink-0 max-[359px]:mr-1 max-[359px]:h-[22px] tablet:mr-4 tablet:h-8"
        />
        <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-[#5CFF7A]">
          {banner.live && <span className="h-2 w-2 animate-pulse rounded-full bg-[#FF3B3B]" />}
          {banner.badge}
        </span>
        <span className="hidden desktop:inline">{banner.texts.md}</span>
        <span className="hidden tablet:inline desktop:hidden">{banner.texts.sm}</span>
        <span className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-[#5CFF7A] px-3 py-1 text-xs font-bold text-[#06122B] tablet:text-sm">
          {banner.texts.buttonText}
          <LinkExternal aria-hidden="true" width={14} height={14} className="shrink-0 max-[359px]:hidden" />
        </span>
      </a>
      <button
        type="button"
        aria-label="Fechar aviso"
        onClick={close}
        className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer border-0 bg-transparent p-2 text-xl leading-none text-[#D8E2FB] tablet:right-4">
        ×
      </button>
      <div className="absolute bottom-0 left-0 h-[3px] w-full bg-[linear-gradient(90deg,#2B80FD,#46BFBB,#61FE7A)]" />
    </section>
  );
}
